import { useEffect, useState, useRef } from 'react'
import axios from 'axios'
import type { Session } from '@supabase/supabase-js'
import { AdaptiveLogsPanel } from './AdaptiveLogsPanel'

// Phase 7.2: Retrieval Control Plane - Read-Only UI

const API_BASE = import.meta.env.VITE_RAG_API_URL
const NETWORK_TIMEOUT_MS = 15000 // 15 seconds strict timeout

interface GlobalConfig {
    threshold_policy: string
    semantic_threshold: number
    cosine_threshold: number
    min_chunks: number
    rerank_enabled: boolean
    rerank_fallback: string
    log_policy_decisions: boolean
    source: string
}

interface TenantPolicy {
    tenant_id: string
    threshold_policy: string
    semantic_threshold: number | null
    cosine_threshold: number | null
    min_chunks: number
    rerank_enabled: boolean
    rerank_fallback: string
    created_at: string | null
    updated_at: string | null
}

interface AdaptiveSummary {
    intent_aware_enabled: boolean
    recent_adjustments: any[]
    threshold_suggestions: any
}

interface RetrievalControlPlaneProps {
    session: Session | null
}

export function RetrievalControlPlane({ session }: RetrievalControlPlaneProps) {
    // State for individual sections (Command 4: Partial UI > Blocked UI)
    const [globalConfig, setGlobalConfig] = useState<GlobalConfig | null>(null)
    const [globalConfigError, setGlobalConfigError] = useState<string | null>(null)

    const [tenantPolicies, setTenantPolicies] = useState<TenantPolicy[]>([])
    const [tenantPoliciesError, setTenantPoliciesError] = useState<string | null>(null)

    const [adaptiveSummary, setAdaptiveSummary] = useState<AdaptiveSummary | null>(null)
    const [adaptiveSummaryError, setAdaptiveSummaryError] = useState<string | null>(null)

    const [loading, setLoading] = useState(true)
    const [opsSystemDisabled, setOpsSystemDisabled] = useState(false) // 503 Service Unavailable only

    // Guard against race conditions using a mount ref
    const isMounted = useRef(true)

    useEffect(() => {
        isMounted.current = true
        if (session?.access_token) {
            loadData()
        } else {
            setLoading(false)
        }
        return () => {
            isMounted.current = false
        }
    }, [session])

    const loadData = async () => {
        if (!session?.access_token) return

        try {
            setLoading(true)
            setOpsSystemDisabled(false)

            const config = {
                headers: { Authorization: `Bearer ${session.access_token}` },
                timeout: NETWORK_TIMEOUT_MS // Structural Fix: Prevent infinite hangs
            }

            console.log('[RetrievalControlPlane] Starting sequential fetch with timeout', NETWORK_TIMEOUT_MS)

            // 1. Global Config
            try {
                const res = await axios.get(`${API_BASE}/ops/retrieval/config`, config)
                if (isMounted.current) {
                    console.log('[RetrievalControlPlane] Global Config loaded')
                    setGlobalConfig(res.data)
                    setGlobalConfigError(null)
                }
            } catch (err: any) {
                console.error('Failed to load global config:', err.message)
                if (err.response?.status === 503) {
                    if (isMounted.current) {
                        setOpsSystemDisabled(true)
                    }
                    return // finally block will run
                }
                if (isMounted.current) {
                    // Check for timeout explicitly
                    const msg = err.code === 'ECONNABORTED' ? 'Network Timeout' : err.message
                    setGlobalConfigError(msg)
                }
            }

            if (!isMounted.current) return

            // 2. Tenant Policies
            try {
                const res = await axios.get(`${API_BASE}/ops/retrieval/policies`, config)
                if (isMounted.current) {
                    console.log('[RetrievalControlPlane] Policies loaded')
                    setTenantPolicies(res.data.policies || [])
                    setTenantPoliciesError(null)
                }
            } catch (err: any) {
                console.error('Failed to load policies:', err)
                if (isMounted.current) {
                    setTenantPoliciesError('Failed to load policies (Access Denied or Error)')
                }
            }

            if (!isMounted.current) return

            // 3. Adaptive Summary
            try {
                const res = await axios.get(`${API_BASE}/ops/retrieval/adaptive/summary`, config)
                if (isMounted.current) {
                    console.log('[RetrievalControlPlane] Adaptive Summary loaded')
                    setAdaptiveSummary(res.data)
                    setAdaptiveSummaryError(null)
                }
            } catch (err: any) {
                console.error('Failed to load adaptive summary:', err)
                if (isMounted.current) {
                    setAdaptiveSummaryError('Access Denied (Requires Admin/Ops role)')
                }
            }

        } catch (e) {
            console.error('[RetrievalControlPlane] Critical loadData error:', e)
        } finally {
            if (isMounted.current) {
                console.log('[RetrievalControlPlane] Finished loading')
                setLoading(false)
            }
        }
    }

    if (loading) {
        return (
            <div className="p-8 text-center text-gray-400">Loading configuration...</div>
        )
    }

    // Command 7: Only 503 (Feature Flag) blocks the specific feature UI
    if (opsSystemDisabled) {
        return (
            <div className="p-8">
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-6">
                    <h3 className="text-yellow-500 font-semibold mb-2">⚠️ Ops Routes Disabled</h3>
                    <p className="text-gray-300">
                        The ops configuration system is currently disabled by administrator.
                        (ENABLE_OPS_ROUTES=false)
                    </p>
                </div>
            </div>
        )
    }

    return (
        <div className="p-8 space-y-8">
            <div>
                <h2 className="text-2xl font-bold text-white mb-2">Retrieval Control Plane</h2>
                <p className="text-gray-400">Read-only view of Phase 6 retrieval configuration and policies</p>
            </div>

            {/* Global Configuration */}
            {globalConfigError ? (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6">
                    <p className="text-red-400">Failed to load global configuration: {globalConfigError}</p>
                </div>
            ) : (
                <section className="bg-white/5 border border-white/10 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-white mb-4">Global Configuration</h3>
                    <div className="grid grid-cols-2 gap-4">
                        <ConfigField label="Threshold Policy" value={globalConfig?.threshold_policy || 'N/A'} badge />
                        <ConfigField label="Semantic Threshold" value={globalConfig?.semantic_threshold.toFixed(2) || 'N/A'} />
                        <ConfigField label="Cosine Threshold" value={globalConfig?.cosine_threshold.toFixed(2) || 'N/A'} />
                        <ConfigField label="Min Chunks" value={globalConfig?.min_chunks.toString() || 'N/A'} />
                        <ConfigField label="Re-rank Enabled" value={globalConfig?.rerank_enabled ? '✓ Yes' : '✗ No'} />
                        <ConfigField label="Re-rank Fallback" value={globalConfig?.rerank_fallback || 'N/A'} />
                        <ConfigField label="Log Policy Decisions" value={globalConfig?.log_policy_decisions ? '✓ Yes' : '✗ No'} />
                        <ConfigField label="Source" value={globalConfig?.source || 'N/A'} />
                    </div>
                </section>
            )}

            {/* Tenant Policies */}
            {tenantPoliciesError ? (
                <div className="bg-white/5 border border-white/10 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-white mb-4">Tenant Policy Overrides</h3>
                    <p className="text-orange-400 text-sm">⚠️ {tenantPoliciesError}</p>
                </div>
            ) : (
                <section className="bg-white/5 border border-white/10 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-white mb-4">Tenant Policy Overrides</h3>
                    {tenantPolicies.length === 0 ? (
                        <p className="text-gray-400">No tenant-specific policies configured</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="border-b border-white/10">
                                    <tr>
                                        <th className="pb-2 text-gray-400 font-medium">Tenant ID</th>
                                        <th className="pb-2 text-gray-400 font-medium">Policy</th>
                                        <th className="pb-2 text-gray-400 font-medium">Semantic</th>
                                        <th className="pb-2 text-gray-400 font-medium">Cosine</th>
                                        <th className="pb-2 text-gray-400 font-medium">Min Chunks</th>
                                        <th className="pb-2 text-gray-400 font-medium">Updated</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tenantPolicies.map((policy) => (
                                        <tr key={policy.tenant_id} className="border-b border-white/5">
                                            <td className="py-3 text-gray-300 font-mono text-sm">{policy.tenant_id.slice(0, 8)}...</td>
                                            <td className="py-3">
                                                <PolicyBadge policy={policy.threshold_policy} />
                                            </td>
                                            <td className="py-3 text-gray-300">{policy.semantic_threshold?.toFixed(2) || 'default'}</td>
                                            <td className="py-3 text-gray-300">{policy.cosine_threshold?.toFixed(2) || 'default'}</td>
                                            <td className="py-3 text-gray-300">{policy.min_chunks}</td>
                                            <td className="py-3 text-gray-400 text-sm">{policy.updated_at ? new Date(policy.updated_at).toLocaleDateString() : 'N/A'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            )}

            {/* Adaptive Strategies */}
            {adaptiveSummaryError ? (
                <div className="bg-white/5 border border-white/10 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-white mb-4">Adaptive Strategies</h3>
                    <p className="text-orange-400 text-sm">⚠️ {adaptiveSummaryError}</p>
                </div>
            ) : (
                <section className="bg-white/5 border border-white/10 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-white mb-4">Adaptive Strategies</h3>
                    <div className="space-y-4">
                        <ConfigField
                            label="Intent-Aware Adjustment"
                            value={adaptiveSummary?.intent_aware_enabled ? '✓ Enabled' : '✗ Disabled'}
                        />
                        <div>
                            <p className="text-gray-400 text-sm mb-2">Recent Adjustments</p>
                            {adaptiveSummary?.recent_adjustments.length === 0 ? (
                                <p className="text-gray-500 text-sm">No recent adjustments (Phase 8+ will log these)</p>
                            ) : (
                                <div className="text-gray-300">
                                    {/* Phase 8+: Display recent adjustments */}
                                </div>
                            )}
                        </div>
                        <div>
                            <p className="text-gray-400 text-sm mb-2">Threshold Suggestions</p>
                            <p className="text-gray-500 text-sm">
                                Suggestions are logged but not persisted (Phase 8+ feature)
                            </p>
                        </div>
                    </div>
                </section>
            )}

            {/* Phase 8.2: Adaptive Logs */}
            <AdaptiveLogsPanel session={session} />

            <div className="text-center text-gray-500 text-sm">
                <p>⚠️ This is a read-only view. Configuration changes require direct environment/database updates.</p>
            </div>
        </div>
    )
}

function ConfigField({ label, value, badge = false }: { label: string; value: string; badge?: boolean }) {
    return (
        <div>
            <p className="text-gray-400 text-sm mb-1">{label}</p>
            {badge ? (
                <PolicyBadge policy={value} />
            ) : (
                <p className="text-white font-medium">{value}</p>
            )}
        </div>
    )
}

function PolicyBadge({ policy }: { policy: string }) {
    const colors = {
        strict: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
        balanced: 'bg-green-500/20 text-green-400 border-green-500/30',
        permissive: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
    }

    const color = colors[policy as keyof typeof colors] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'

    return (
        <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium border ${color}`}>
            {policy}
        </span>
    )
}
